import type { ProgramFixedContentRowUpsert } from '../types/domain';
import { normalizeFixedContentRowForStorage } from './programContentNullNormalize';

/**
 * 프로그램 내용 저장(고정·편제·프로젝트 공통): 기준본(마지막 서버 목록)과 화면 스냅샷을 비교해
 * 바뀐 행·새 행·사라진 행만 골라낸다. 화면이 곧 저장본이 되도록 하되 요청량은 차이만큼만.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isProgramContentRowUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value.trim());
}

const CONTENT_TEXT_FIELDS = [
  'event_name',
  'timetable_text',
  'unit_text',
  'learning_text',
  'learning_note',
  'page_text',
  'assistant_page_text',
  'current_period_text',
  'total_period_text',
  'material_text',
  'record_cross_curricular_text',
  'record_memo_text',
  'record_evaluation_text',
  'record_class_text',
] as const;

const INHERITABLE_FIELDS = [
  ...CONTENT_TEXT_FIELDS,
  'presentation_ref_id',
  'worksheet_ref_id',
  'record_member_group_id',
  'bg_color_by_field',
] as const;

export type ProgramContentDiffRowLike = {
  id?: string | null;
  order_index?: number;
  source_annual_cell_key?: string | null;
  created_at?: string;
  learning_text?: string | null;
  learning_note?: string | null;
  presentation_ref_id?: string | null;
  worksheet_ref_id?: string | null;
};

function text(value: unknown): string {
  return String(value ?? '').trim();
}

function uuidOrEmpty(value: unknown): string {
  const s = text(value).toLowerCase();
  return UUID_RE.test(s) ? s : '';
}

function stableBg(bg: unknown): Array<[string, string]> {
  if (!bg || typeof bg !== 'object') return [];
  return Object.entries(bg as Record<string, unknown>)
    .filter(([, v]) => !!text(v))
    .map(([k, v]) => [k, text(v)] as [string, string])
    .sort((a, b) => a[0].localeCompare(b[0]));
}

function normalizeForCompare(row: ProgramContentDiffRowLike): Record<string, unknown> {
  return normalizeFixedContentRowForStorage(row as unknown as ProgramFixedContentRowUpsert) as unknown as Record<
    string,
    unknown
  >;
}

/** 순서·슬롯 키를 뺀 저장 내용 시그니처 */
export function programContentRowContentDigest(row: ProgramContentDiffRowLike): string {
  const n = normalizeForCompare(row);
  return JSON.stringify([
    ...CONTENT_TEXT_FIELDS.map((f) => text(n[f])),
    uuidOrEmpty(n.presentation_ref_id),
    uuidOrEmpty(n.worksheet_ref_id),
    uuidOrEmpty(n.record_member_group_id),
    stableBg(n.bg_color_by_field),
  ]);
}

function positionDigest(row: ProgramContentDiffRowLike): string {
  return `${row.order_index ?? 0}\u0001${text(row.source_annual_cell_key)}`;
}

export type ProgramContentDiffChange<T> = {
  clientKey: string;
  row: T;
  /** 내용은 같고 순서·슬롯 키만 바뀐 기존 행 */
  positionOnly: boolean;
};

export type ProgramContentDiffPlan<T> = {
  /** id 정리(uuid만)·슬롯 키 중복 정리된 전체 스냅샷 */
  snapshot: Array<{ clientKey: string; row: T }>;
  changes: ProgramContentDiffChange<T>[];
  deleteIds: string[];
};

/**
 * - 스냅샷 id: uuid만 인정(temp-/virtual-는 새 행). 같은 id가 두 번 나오면 뒤쪽은 새 행.
 * - 슬롯 키가 겹치면 앞 행이 키를 갖고 뒤 행은 넘침(null) — 내용은 버리지 않는다.
 * - 삭제: 기준본에 있던 id 중 스냅샷에 없는 것만(기준본에 없던 서버 행은 건드리지 않음).
 */
export function planProgramContentRowsDiff<T extends ProgramContentDiffRowLike>(
  baseline: readonly ProgramContentDiffRowLike[],
  snapshotRows: readonly T[]
): ProgramContentDiffPlan<T> {
  const baselineById = new Map<string, ProgramContentDiffRowLike>();
  baseline.forEach((row) => {
    const id = text(row.id);
    if (isProgramContentRowUuid(id)) baselineById.set(id.toLowerCase(), row);
  });

  const seenIds = new Set<string>();
  const seenKeys = new Set<string>();
  const snapshot = snapshotRows.map((raw, idx) => {
    const rawId = text(raw.id).toLowerCase();
    const id = isProgramContentRowUuid(rawId) && !seenIds.has(rawId) ? rawId : undefined;
    if (id) seenIds.add(id);
    const key = text(raw.source_annual_cell_key);
    const keepKey = !!key && !seenKeys.has(key);
    if (keepKey) seenKeys.add(key);
    const row = {
      ...raw,
      id,
      source_annual_cell_key: keepKey ? key : null,
    } as T;
    return { clientKey: `r${idx}`, row };
  });

  /** 화면이 안 다루는 칸(키 자체가 없음)은 기준본 값을 이어받는다 — 저장 시 지워지거나 변경으로 잡히지 않게 */
  snapshot.forEach((entry) => {
    const base = entry.row.id ? baselineById.get(entry.row.id) : undefined;
    if (!base) return;
    const baseRecord = base as Record<string, unknown>;
    const rowRecord = entry.row as unknown as Record<string, unknown>;
    const filled: Record<string, unknown> = {};
    INHERITABLE_FIELDS.forEach((f) => {
      if (rowRecord[f] === undefined && baseRecord[f] !== undefined) filled[f] = baseRecord[f];
    });
    if (Object.keys(filled).length) entry.row = { ...entry.row, ...filled } as T;
  });

  const changes: ProgramContentDiffChange<T>[] = [];
  snapshot.forEach(({ clientKey, row }) => {
    const base = row.id ? baselineById.get(row.id) : undefined;
    if (!base) {
      changes.push({ clientKey, row, positionOnly: false });
      return;
    }
    const contentSame = programContentRowContentDigest(base) === programContentRowContentDigest(row);
    const positionSame = positionDigest(base) === positionDigest(row);
    if (contentSame && positionSame) return;
    changes.push({ clientKey, row, positionOnly: contentSame });
  });

  const deleteIds = Array.from(baselineById.keys()).filter((id) => !seenIds.has(id));
  return { snapshot, changes, deleteIds };
}

/** 저장 결과를 기준본에 반영: 삭제 id 제거 → 저장 행으로 교체/추가(합치기 아님) */
export function applyProgramContentRowsDiff<T extends ProgramContentDiffRowLike & { order_index: number }>(
  baseline: readonly T[],
  savedRows: readonly T[],
  deleteIds: readonly string[]
): T[] {
  const deleted = new Set(deleteIds.map((id) => id.toLowerCase()));
  const byId = new Map<string, T>();
  baseline.forEach((row) => {
    const id = text(row.id).toLowerCase();
    if (!id || deleted.has(id)) return;
    byId.set(id, row);
  });
  savedRows.forEach((row) => {
    const id = text(row.id).toLowerCase();
    if (id) byId.set(id, row);
  });
  /** 저장 행이 가져간 슬롯 키를 옛 행이 들고 있으면(서버 3단계와 동일) 넘침으로 내린다 */
  const savedIds = new Set(savedRows.map((row) => text(row.id).toLowerCase()));
  const savedKeys = new Set(savedRows.map((row) => text(row.source_annual_cell_key)).filter(Boolean));
  const out = Array.from(byId.values()).map((row) => {
    const id = text(row.id).toLowerCase();
    const key = text(row.source_annual_cell_key);
    if (!savedIds.has(id) && key && savedKeys.has(key)) return { ...row, source_annual_cell_key: null };
    return row;
  });
  return out.sort(
    (a, b) =>
      (a.order_index - b.order_index) || String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''))
  );
}

export type ProgramContentRemoteAttachmentRefs = {
  presentation_ref_id?: string | null;
  worksheet_ref_id?: string | null;
};

export type ProgramContentAttachmentRefRestore = {
  clientKey: string;
  presentationRefId?: string;
  worksheetRefId?: string;
};

/**
 * 다른 탭·기기가 기준본 이후에 연결한 첨부(프레젠테이션·학습지)를 오래된 화면 값으로 지우지 않도록,
 * 이 화면에서 바꾸지 않은 첨부 ref는 서버 현재 값으로 되돌릴 목록을 만든다.
 * (화면에서 직접 바꾼 ref·서버가 비어 있는 경우는 화면 값 유지)
 */
export function findProgramContentAttachmentRefRestores<T extends ProgramContentDiffRowLike>(
  baseline: readonly ProgramContentDiffRowLike[],
  plan: ProgramContentDiffPlan<T>,
  remoteById: ReadonlyMap<string, ProgramContentRemoteAttachmentRefs>
): ProgramContentAttachmentRefRestore[] {
  const baselineById = new Map<string, ProgramContentDiffRowLike>();
  baseline.forEach((row) => {
    const id = text(row.id).toLowerCase();
    if (isProgramContentRowUuid(id)) baselineById.set(id, row);
  });
  const restores: ProgramContentAttachmentRefRestore[] = [];
  plan.changes.forEach(({ clientKey, row, positionOnly }) => {
    if (positionOnly) return;
    const id = text(row.id).toLowerCase();
    const base = id ? baselineById.get(id) : undefined;
    const remote = id ? remoteById.get(id) : undefined;
    if (!base || !remote) return;
    const b = normalizeForCompare(base);
    const s = normalizeForCompare(row);
    const restore: ProgramContentAttachmentRefRestore = { clientKey };
    const pick = (col: 'presentation_ref_id' | 'worksheet_ref_id'): string | undefined => {
      const snapRef = uuidOrEmpty(s[col]);
      if (snapRef !== uuidOrEmpty(b[col])) return undefined;
      const remoteRef = uuidOrEmpty(remote[col]);
      return remoteRef && remoteRef !== snapRef ? remoteRef : undefined;
    };
    const pres = pick('presentation_ref_id');
    const ws = pick('worksheet_ref_id');
    if (pres) restore.presentationRefId = pres;
    if (ws) restore.worksheetRefId = ws;
    if (pres || ws) restores.push(restore);
  });
  return restores;
}

export type ProgramContentRefFork = {
  clientKey: string;
  kind: 'presentation' | 'worksheet';
  fromRef: string;
};

/**
 * 증배·복사로 두 행이 같은 프레젠테이션/학습지를 가리키면, 원본(기준본에서 그 첨부를 갖던 행 → 없으면 첫 행)만 두고
 * 이번에 바뀐 나머지 행은 사본으로 분리해야 한다. 바뀌지 않은 행끼리의 기존 공유는 건드리지 않는다.
 */
export function findProgramContentRefForks<T extends ProgramContentDiffRowLike>(
  baseline: readonly ProgramContentDiffRowLike[],
  plan: ProgramContentDiffPlan<T>
): ProgramContentRefFork[] {
  const changedKeys = new Set(plan.changes.map((c) => c.clientKey));
  const baselineRefsById = new Map<string, { presentation: string; worksheet: string }>();
  baseline.forEach((row) => {
    const n = normalizeForCompare(row);
    baselineRefsById.set(text(row.id).toLowerCase(), {
      presentation: uuidOrEmpty(n.presentation_ref_id),
      worksheet: uuidOrEmpty(n.worksheet_ref_id),
    });
  });
  const forks: ProgramContentRefFork[] = [];
  (['presentation', 'worksheet'] as const).forEach((kind) => {
    const groups = new Map<string, Array<{ clientKey: string; id: string }>>();
    plan.snapshot.forEach(({ clientKey, row }) => {
      const n = normalizeForCompare(row);
      const ref = uuidOrEmpty(kind === 'presentation' ? n.presentation_ref_id : n.worksheet_ref_id);
      if (!ref) return;
      const list = groups.get(ref) ?? [];
      list.push({ clientKey, id: text(row.id).toLowerCase() });
      groups.set(ref, list);
    });
    groups.forEach((list, ref) => {
      if (list.length < 2) return;
      const owner = list.find((m) => m.id && baselineRefsById.get(m.id)?.[kind] === ref) ?? list[0];
      list.forEach((m) => {
        if (m === owner || !changedKeys.has(m.clientKey)) return;
        forks.push({ clientKey: m.clientKey, kind, fromRef: ref });
      });
    });
  });
  return forks;
}
